import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTheme } from 'styled-components';

import {
    QUERIES_ADMIN,
    ROUTES_ACCOUNT,
    ROUTES_ADMIN,
} from '@constants';

import { useRealState } from 'contexts/RealStateContext';

import { Button, HelperOptions } from 'components/atoms';
import { Table } from 'components/organisms';
import { onErrorMessage, onSuccessMessage } from 'helpers';
import {
    getAdministratorPricesIndexsIndex,
    removeAdministratorPricesIndexs,
} from 'services';
import { sweetAlert } from 'utils';

import { PricesIndexsListWrap } from './styles';
import Layout from '../../Layout';

const PricesIndexsList = () => {
    const titleMessages = 'Índices de Preços!';
    const theme = useTheme();
    const navigate = useNavigate();
    const { getLinkWithSlug } = useRealState();

    const queryClient = useQueryClient();

    const { data, isLoading: isLoadingList } = useQuery(
        QUERIES_ADMIN.PRICES_INDEXS.base,
        () => getAdministratorPricesIndexsIndex()
    );

    const { mutate: removeRegister } =
        useMutation((id: number) => removeAdministratorPricesIndexs(String(id)), {
            mutationKey: QUERIES_ADMIN.PRICES_INDEXS.base,
            onSuccess: () => {
                onSuccessMessage(
                    titleMessages,
                    'Registro removido com sucesso!'
                );

                queryClient.invalidateQueries(QUERIES_ADMIN.PRICES_INDEXS.base);
            },
            onError: (err) => {
                onErrorMessage(titleMessages, err);
            },
        });

    function onHandleNavigate(param: string) {
        navigate(param);
    }

    function newRegister() {
        onHandleNavigate(getLinkWithSlug(ROUTES_ADMIN.PRICES_INDEXS.FORM.fullPath));
    }

    function editRegister(param: string) {
        onHandleNavigate(
            getLinkWithSlug(`${ROUTES_ADMIN.PRICES_INDEXS.FORM.fullPath}/${param}`)
        );
    }

    function removeRegisterRequest(id: number) {
        sweetAlert
            .fire({
                title: 'Você tem certeza?',
                text: 'Se você confirmar este registro será removido e está ação não poderá ser revertida!',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: theme.colors.primary,
                cancelButtonColor: theme.colors.danger,
                confirmButtonText: 'Sim, remover registro!',
                cancelButtonText: 'Não, cliquei errado!',
            })
            .then((result) => {
                if (result.isConfirmed) {
                    removeRegister(id);
                }
            });
    }

    return (
        <Layout
            titlePage="Painel Administrativo"
            breadcrumbs={[
                {
                    label: 'Home',
                    href: getLinkWithSlug(ROUTES_ACCOUNT.SELECTED.fullPath),
                },
                {
                    label: 'Índices de Preços',
                    href: getLinkWithSlug(ROUTES_ADMIN.PRICES_INDEXS.fullPath),
                },
            ]}
            title="Índices de Preços"
            buttonTitle={
                <Button color="nonary" onClick={() => newRegister()}>
                    Novo Registro
                </Button>
            }
        >
            <PricesIndexsListWrap className="prices-indexs-template">
                <Table
                    isLoading={isLoadingList}
                    columns={[
                        {
                            dataField: 'id',
                            text: 'ID',
                            sort: true,
                            style: { fontWeight: 700 }
                        },
                        {
                            dataField: 'tipo',
                            text: 'Tipo',
                            sort: true,
                            headerStyle: { width: '100%' }
                        },
                        {
                            dataField: 'ano',
                            text: 'Ano',
                            sort: true,
                            headerStyle: { width: '100%' }
                        },
                        {
                            dataField: 'mes',
                            text: 'Mês',
                            sort: true,
                            headerStyle: { width: '100%' }
                        },
                        {
                            dataField: 'valor',
                            text: 'Valor',
                            sort: true,
                            headerStyle: { width: '100%' }
                        },
                        {
                            dataField: 'valorDozeMeses',
                            text: 'Variação dos últimos 12 meses',
                            sort: true,
                            headerStyle: { width: '100%' }
                        },
                        {
                            dataField: 'id',
                            text: 'Opções',
                            formatter: (fieldValue) =>
                                HelperOptions({
                                    edit: true,
                                    editFunction: () =>
                                        editRegister(fieldValue),
                                    removeFunction: () =>
                                        removeRegisterRequest(fieldValue),
                                }),
                            headerStyle: { width: '10%' }
                        },
                    ]}
                    data={data}
                />
            </PricesIndexsListWrap>
        </Layout>
    );
};

export default PricesIndexsList;
